import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, TrendingUp, Activity, Building2, Contact, Handshake } from "lucide-react";

/**
 * Growth Overview widget shown at the top of the SuperAdmin page.
 * KPIs + a 14-day signup sparkline (inline SVG — no chart lib).
 */
export default function GrowthOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/superadmin/overview")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="border border-[var(--peak-ink)] bg-white p-4 mb-6 text-xs text-[var(--peak-muted)]" data-testid="growth-overview-loading">
        Loading platform metrics…
      </div>
    );
  }

  const kpis = [
    { label: "Workspaces", value: data.total_workspaces, icon: Building2, color: "text-[var(--peak-ink)]" },
    { label: "New · 7d", value: data.new_7d, icon: TrendingUp, color: "text-[var(--peak-accent)]" },
    { label: "New · 30d", value: data.new_30d, icon: TrendingUp, color: "text-[var(--peak-accent)]" },
    { label: "Active · 7d", value: data.active_7d, icon: Activity, color: "text-[var(--peak-blue-deep)]" },
    { label: "Seats", value: data.total_seats, icon: Users, color: "text-[var(--peak-ink)]" },
    { label: "Contacts", value: data.total_contacts, icon: Contact, color: "text-[var(--peak-ink)]" },
    { label: "Deals", value: data.total_deals, icon: Handshake, color: "text-[var(--peak-ink)]" },
  ];

  return (
    <div className="mb-8" data-testid="growth-overview">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div
              key={k.label}
              className="border border-[var(--peak-ink)] bg-white p-4"
              data-testid={`kpi-${k.label.replace(/\s|·/g, "-").toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--peak-muted)]">{k.label}</span>
                <Icon className={`w-3.5 h-3.5 ${k.color}`} />
              </div>
              <div className="text-2xl font-heading font-black tracking-tighter">{k.value}</div>
            </div>
          );
        })}
      </div>

      {/* 14-day signups sparkline */}
      <Sparkline series={data.signups_14d} />
    </div>
  );
}

function Sparkline({ series }) {
  const width = 800;
  const height = 90;
  const padX = 8;
  const padY = 12;
  const max = Math.max(1, ...series.map((s) => s.count));
  const step = (width - 2 * padX) / Math.max(1, series.length - 1);

  const points = series.map((s, i) => {
    const x = padX + i * step;
    const y = height - padY - ((s.count / max) * (height - 2 * padY));
    return { x, y, ...s };
  });

  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaD = `${pathD} L${points[points.length - 1].x},${height - padY} L${points[0].x},${height - padY} Z`;
  const total = series.reduce((a, s) => a + s.count, 0);

  return (
    <div className="border border-[var(--peak-ink)] bg-white p-4" data-testid="signups-sparkline">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--peak-muted)]">Signups · Last 14 days</div>
          <div className="text-lg font-heading font-black tracking-tighter">{total} new workspaces</div>
        </div>
        <div className="text-[10px] font-mono text-[var(--peak-muted)]">
          {series[0].date} → {series[series.length - 1].date}
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-24" preserveAspectRatio="none">
        <path d={areaD} fill="var(--peak-accent)" opacity="0.15" />
        <path d={pathD} fill="none" stroke="var(--peak-accent)" strokeWidth="2" />
        {points.map((p) => (
          <circle key={p.date} cx={p.x} cy={p.y} r={p.count > 0 ? 3 : 1.5} fill={p.count > 0 ? "var(--peak-accent)" : "var(--peak-border)"} />
        ))}
      </svg>
    </div>
  );
}
